export interface Testimonial {
  text: string;
  name: string;
  location: string;
  rating: number;
}

export interface TestimonialsContent {
  eyebrow: string;
  headline: string;
  overallRating: string;
  reviewCount: string;
  testimonials: Testimonial[];
}

interface Props {
  content: TestimonialsContent;
}

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <i key={i} className={`text-[13px] ${i < count ? 'ri-star-fill text-gold' : 'ri-star-line text-gold/25'}`} />
    ))}
  </div>
);

const TestimonialsSection = ({ content }: Props) => {
  const { eyebrow, headline, overallRating, reviewCount, testimonials } = content;
  const featured = testimonials[0];
  const secondary = testimonials.slice(1);

  return (
    <section className="relative py-40 overflow-hidden" style={{ background: 'linear-gradient(148deg, #0A1520 0%, #0D1B2A 55%, #0B1825 100%)' }}>

      {/* Top and bottom accent lines */}
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(26,74,82,0.5) 28%, rgba(26,74,82,0.5) 72%, transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.3) 30%, rgba(201,168,76,0.3) 70%, transparent)' }} />

      {/* Radial glow */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(26,74,82,0.12) 0%, transparent 65%)' }} />

      <div className="relative z-10 max-w-[1280px] mx-auto px-8">

        {/* ── HEADER ───────────────────────────────────── */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-gold" />
              <span className="text-gold text-[10px] tracking-[0.35em] uppercase font-sans font-semibold">
                {eyebrow}
              </span>
            </div>
            <h2
              className="font-serif text-white font-bold leading-tight"
              style={{ fontSize: 'clamp(2.2rem, 3.8vw, 3.2rem)' }}
            >
              {headline}
            </h2>
            <div className="flex items-center gap-2.5 mt-5">
              <div className="h-[3px] w-14 bg-teal rounded-full" />
              <div className="h-[3px] w-4 rounded-full bg-teal/30" />
            </div>
          </div>

          {/* Rating badge */}
          <div
            className="flex items-center gap-5 px-7 py-4 rounded-2xl flex-shrink-0"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)', backdropFilter: 'blur(18px)' }}
          >
            <Stars count={5} />
            <div className="h-5 w-px bg-white/12" />
            <div>
              <div className="font-serif text-white font-bold text-[1.6rem] leading-none">{overallRating}</div>
              <div className="text-white/70 text-[11px] font-sans mt-0.5">{reviewCount} Verified Reviews</div>
            </div>
            <div className="flex items-center gap-1.5 border-l border-white/10 pl-4">
              <i className="ri-google-fill text-white/60 text-base" />
              <span className="text-white/60 text-[11px] font-sans">Google</span>
            </div>
          </div>
        </div>

        {/* ── FEATURED PULLQUOTE ───────────────────────── */}
        <div
          className="relative rounded-3xl overflow-hidden mb-5 group"
          style={{ background: 'rgba(255,255,255,0.09)', border: '1px solid rgba(255,255,255,0.16)', backdropFilter: 'blur(22px)' }}
        >
          <div className="absolute top-0 left-20 right-20 h-px"
            style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.6), transparent)' }} />
          <div className="absolute top-7 right-9 w-2 h-2 rounded-full bg-gold/30 group-hover:bg-gold/60 transition-colors" />

          <div className="px-10 py-12 lg:px-16 lg:py-14">
            <div className="flex flex-col lg:flex-row lg:items-start gap-8">
              <div
                className="font-serif text-gold/12 font-bold leading-none flex-shrink-0 select-none group-hover:text-gold/22 transition-colors duration-500"
                style={{ fontSize: '9rem', marginTop: '-1.5rem', lineHeight: 1 }}
                aria-hidden="true"
              >
                &ldquo;
              </div>
              <div className="flex-1">
                <p
                  className="text-white font-sans leading-[2.1] mb-9"
                  style={{ fontSize: 'clamp(15px, 1.4vw, 19px)' }}
                >
                  {featured.text}
                </p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-6 border-t border-white/[0.08]">
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 flex items-center justify-center rounded-full flex-shrink-0"
                      style={{ background: 'rgba(26,74,82,0.3)', border: '1px solid rgba(26,74,82,0.5)' }}>
                      <i className="ri-user-3-line text-teal-100 text-base" />
                    </div>
                    <div>
                      <div className="font-sans font-bold text-white text-[15px]">{featured.name}</div>
                      <div className="text-white/80 text-[12px] font-sans mt-0.5 flex items-center gap-1.5">
                        <i className="ri-map-pin-line text-gold/60 text-[10px]" />
                        {featured.location}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start sm:items-end gap-1.5">
                    <Stars count={featured.rating} />
                    <span className="text-[10.5px] text-white/70 font-sans uppercase tracking-wide">Verified Customer</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── SECONDARY CARDS ──────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {secondary.map((t, idx) => (
            <div
              key={idx}
              className="relative rounded-2xl p-9 flex flex-col transition-all duration-500 group"
              style={{
                background: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.13)',
                backdropFilter: 'blur(18px)',
                marginTop: idx === 0 ? '0' : '1.5rem',
              }}
            >
              <div className="absolute top-0 left-12 right-12 h-px"
                style={{ background: 'linear-gradient(90deg, transparent, rgba(26,74,82,0.6), transparent)' }} />

              <div
                className="font-serif text-gold/10 font-bold leading-none mb-3 select-none group-hover:text-gold/20 transition-colors duration-400"
                style={{ fontSize: '5rem' }}
                aria-hidden="true"
              >
                &ldquo;
              </div>
              <p className="text-white text-[14.5px] leading-[1.98] font-sans flex-1 mb-8">{t.text}</p>

              <div className="flex items-center justify-between pt-5 border-t border-white/[0.07]">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: 'rgba(26,74,82,0.25)', border: '1px solid rgba(26,74,82,0.4)' }}>
                    <i className="ri-user-3-line text-teal-100 text-sm" />
                  </div>
                  <div>
                    <div className="font-sans font-bold text-white text-[13.5px]">{t.name}</div>
                    <div className="text-white/75 text-[11px] font-sans mt-0.5 flex items-center gap-1">
                      <i className="ri-map-pin-line text-gold/60 text-[9px]" />
                      {t.location}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Stars count={t.rating} />
                  <span className="text-[10px] text-white/70 font-sans">Verified</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
